import { PartnerPartnerEntitlements } from 'vopay';
import { useCallback, useRef, useState } from 'react';
import { api } from '@/services/api.config';

/**
 * The platform capabilities of one partner account this partner created (ID-13788 item 4): the ten
 * read-only flags `create-partner` copied from the parent's profile.
 */
export default function useFetchPartnerPartnerEntitlements() {
  const [fetchingPartnerEntitlements, setFetchingPartnerEntitlements] = useState(false);
  const [partnerEntitlements, setPartnerEntitlements] = useState<PartnerPartnerEntitlements>();

  /**
   * Only the newest request may write to state. The tab stays mounted as the user moves between
   * partner pages, and nothing cancels the request already in flight — so without this an earlier
   * response landing last would show one partner's flags under another's name.
   */
  const latestRequestId = useRef(0);

  const fetchPartnerPartnerEntitlements = useCallback(async (partnerAccountID: string) => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingPartnerEntitlements(true);
      const res = await api.fetchPartnerPartnerEntitlements({ PartnerAccountID: partnerAccountID });
      if (!res.Success) throw new Error(res.ErrorMessage);
      if (isLatest()) setPartnerEntitlements(res.Entitlements);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingPartnerEntitlements(false);
    }
  }, []);

  const reset = useCallback(() => {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared state.
    latestRequestId.current += 1;
    setPartnerEntitlements(undefined);
    setFetchingPartnerEntitlements(false);
  }, []);

  return { fetchingPartnerEntitlements, partnerEntitlements, fetchPartnerPartnerEntitlements, reset };
}
